import {
  Box,
  Divider,
  Icon,
  InlineStack,
  LegacyStack,
  Pagination,
  ResourceList,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris'
import { SearchIcon } from '@shopify/polaris-icons'
import React, { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'

import { AdaptiveModal } from './AdaptiveModal'
import { backendFetchResult } from './api'
import { GQLPaginator, GQLPaginatorPageInfo } from './gql-models-common'
import { queryString, useDebounced } from './helpers'

export interface ProductResult {
  id: string
  restId: number
  title: string
  image: string | null
}

interface ProductQueryResult {
  search: string
  results: ProductResult[]
  pageInfo: GQLPaginatorPageInfo | null
  beforeCursor: string | null
  afterCursor: string | null
}

const searchProducts = async (
  _: any,
  search: string,
  beforeCursor: string | null,
  afterCursor: string | null
): Promise<ProductQueryResult> => {
  const query = queryString({ query: search, limit: 8, before: beforeCursor, after: afterCursor })
  const res = await backendFetchResult<GQLPaginator<ProductResult>>(
    'GET',
    `/products/search-products?${query}`
  )
  const results = res.status === 'success' && res.data ? res.data.nodes : []
  const pageInfo = res.data?.page ?? null
  return { search, results, pageInfo, beforeCursor, afterCursor }
}

const renderProductResult = (result: ProductResult, onSelect: (result: ProductResult) => any) => {
  return (
    <ResourceList.Item
      id={result.id}
      media={<Thumbnail source={result.image || ''} alt="" size="small" />}
      onClick={() => onSelect(result)}
      verticalAlignment="center"
    >
      <h3>
        <Text as="span" fontWeight="semibold">
          {result.title}
        </Text>
      </h3>
    </ResourceList.Item>
  )
}

interface ModalContentProps {
  search: string
  onSearchChange: (value: string) => void
  items: ProductResult[]
  loading: boolean
  renderItem: (item: ProductResult) => React.ReactNode
}

const ModalContent = ({
  search,
  onSearchChange,
  items,
  loading,
  renderItem,
}: ModalContentProps) => (
  <>
    <Box padding="400">
      <TextField
        label=""
        placeholder="Search"
        prefix={<Icon source={SearchIcon} />}
        value={search}
        onChange={onSearchChange}
        autoComplete="off"
      />
    </Box>
    <Divider />
    <ResourceList
      items={items}
      renderItem={renderItem}
      loading={loading}
      emptyState={
        <LegacyStack distribution="center">
          <p style={{ padding: '2rem' }}>No products found</p>
        </LegacyStack>
      }
    />
  </>
)

interface Props {
  open: boolean
  onClose: () => any
  onSelect: (result: ProductResult) => any
}

export const ProductModal = ({ open, onClose, onSelect }: Props) => {
  const [beforeCursor, setBeforeCursor] = useState<string | null>(null)
  const [afterCursor, setAfterCursor] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, 500)

  const [query, setQuery] = useState<ProductQueryResult>({
    search: '___',
    results: [],
    pageInfo: null,
    beforeCursor: null,
    afterCursor: null,
  })
  const swr = useSWR(
    open ? ['product-variants', debouncedSearch, beforeCursor, afterCursor] : null,
    searchProducts
  )

  const myRenderProductResult = useCallback(
    (result: ProductResult) => renderProductResult(result, onSelect),
    [onSelect]
  )

  useEffect(() => {
    if (
      swr.data &&
      swr.data.search === search &&
      swr.data.beforeCursor === beforeCursor &&
      swr.data.afterCursor === afterCursor
    ) {
      setQuery(swr.data)
    }
  }, [swr.data, search, beforeCursor, afterCursor, setQuery])

  useEffect(() => {
    setBeforeCursor(null)
    setAfterCursor(null)
  }, [search])

  const loading =
    query.search !== search ||
    query.beforeCursor !== beforeCursor ||
    query.afterCursor !== afterCursor

  const pagination =
    query.results.length > 0 && query.pageInfo
      ? {
          hasPrevious: query.pageInfo.hasPreviousPage && !loading,
          hasNext: query.pageInfo.hasNextPage && !loading,
          onPrevious: () => {
            setAfterCursor(null)
            setBeforeCursor(query.pageInfo?.firstCursor ?? null)
          },
          onNext: () => {
            setBeforeCursor(null)
            setAfterCursor(query.pageInfo?.lastCursor ?? null)
          },
        }
      : undefined

  return (
    <AdaptiveModal
      title="Select product"
      open={open}
      onClose={onClose}
      footer={
        pagination && (
          <InlineStack align="center">
            <Pagination
              hasPrevious={pagination.hasPrevious}
              onPrevious={pagination.onPrevious}
              hasNext={pagination.hasNext}
              onNext={pagination.onNext}
            />
          </InlineStack>
        )
      }
    >
      <ModalContent
        search={search}
        onSearchChange={setSearch}
        items={query.results}
        loading={loading}
        renderItem={myRenderProductResult}
      />
    </AdaptiveModal>
  )
}
